// Improve Newco's own Google Analytics 4, loaded through our Tag Manager
// container and ONLY after analytics consent. Nothing here runs at page load
// by itself: trackers.js loads it when the stored or live choice allows it.
//
// Events are plain dataLayer pushes, sent by the container's "GA4 events" tag:
// in its Live environment to the production property, and in every other
// environment (staging builds pass VUE_APP_GTM_ENV_AUTH and
// VUE_APP_GTM_ENV_PREVIEW) to the staging property, with cookies prefixed
// "stg". A Google tag loaded by Tag Manager ignores gtag('event') calls, so
// events must go through that tag. The container, including the dataLayer
// keys the tag reads, is defined in scripts/google-api.js in the tracker
// repo; keep the two in step. Page views come from our router, not Google's
// automatic tracking, so the address can be cleaned first.
//
// Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.

import { hasAnalytics } from '@/utils/consent';

const GTM_ID = process.env.VUE_APP_GTM_ID || '';
const GTM_ENV_AUTH = process.env.VUE_APP_GTM_ENV_AUTH || '';
const GTM_ENV_PREVIEW = process.env.VUE_APP_GTM_ENV_PREVIEW || '';
const SITE = 'website';
// The only query parameters Google may see: campaign tags. Emails, tokens and
// purchase ids in a URL never leave the browser.
const KEPT_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Event keys are cleared on every push, so no event inherits another's. The
// page keys (page_location, page_referrer, page_title, ignore_referrer,
// content_group) stay until the next page view, so every event on a page
// carries its clean address and the kind of page it happened on.
const EVENT_KEYS = ['form', 'method', 'slot', 'payment_type', 'cta', 'cta_location', 'field',
  'search_term', 'search_outcome'];
// What kind of page an address is, for GA4's own "Content group" dimension:
// the first rule whose prefix matches wins. Growth plan section 2
// (docs/plans/ga4-growth-2026-10.md in the tracker repo).
const CONTENT_GROUPS = [
  ['/homepage', 'home'],
  ['/watch-demo', 'demo'],
  ['/experience', 'demo'],
  ['/improve-live', 'webinar'],
  ['/resources/webinars', 'webinar'],
  ['/solutions', 'solutions'],
  ['/compare', 'comparison'],
  ['/why-switch', 'comparison'],
  ['/course-library', 'courses'],
  ['/course/', 'courses'],
  ['/courses/', 'courses'],
  ['/pricing', 'pricing'],
  ['/get-started', 'checkout'],
  ['/buy-now', 'checkout'],
  ['/resources', 'resources'],
  ['/faq', 'resources'],
  ['/blog', 'blog'],
  ['/about-us', 'company'],
  ['/customer-stories', 'company'],
  ['/partner-programme', 'company'],
  ['/contact', 'contact'],
  ['/thank-you', 'contact'],
  ['/privacy-policy', 'legal'],
  ['/eula', 'legal'],
  ['/usage-policy', 'legal'],
  ['/features', 'product'],
  ['/how-improve-helps', 'product'],
  ['/why-improve', 'product'],
  ['/leadership-plus', 'product'],
  ['/launch-programme', 'product'],
  ['/hr-integrations', 'product'],
  ['/security-gdpr', 'product'],
];
// Conversion links, by where they go. A click on one is sent as cta_click
// with the CTA and where it sits; never the link's text, number or address.
const CTA_RULES = [
  [(u) => /^#\/signup/.test(u.hash), 'start_trial'],
  [(u) => /^#\/login/.test(u.hash), 'sign_in'],
  [(u) => u.protocol === 'tel:', 'phone'],
  [(u) => u.protocol === 'mailto:', 'email'],
  [(u) => /(^|\.)linkedin\.com$/.test(u.hostname), 'social_linkedin'],
  [(u) => /(^|\.)(x|twitter)\.com$/.test(u.hostname), 'social_x'],
  [(u) => /(^|\.)facebook\.com$/.test(u.hostname), 'social_facebook'],
  [(u) => /(^|\.)instagram\.com$/.test(u.hostname), 'social_instagram'],
  [(u) => /(^|\.)youtube\.com$/.test(u.hostname), 'social_youtube'],
  [(u) => /^\/(get-started|buy-now)\/?$/.test(u.pathname), 'buy'],
  [(u) => /^\/experience\/?$/.test(u.pathname), 'experience'],
  [(u) => /^\/improve-live\/?$/.test(u.pathname), 'improve_live'],
  [(u) => /^\/watch-demo\/?$/.test(u.pathname), 'watch_demo'],
  [(u) => /^\/contact\/?$/.test(u.pathname), 'contact'],
  [(u) => /^\/pricing\/?$/.test(u.pathname), 'pricing'],
];
// A browser a member of staff marked with /?improve_staff=on: its events carry
// traffic_type=internal, which GA4's Internal traffic data filter drops.
const STAFF_KEY = 'improve_staff';
// _ga and _ga_<stream>; on staging stg_ga and stg_ga_<stream> (the container
// sets the cookie prefix "stg").
const GA_COOKIE = /^([A-Za-z0-9]+)?_ga(_[A-Za-z0-9]+)?$/;
const SHARED_DOMAIN = '.improve.online';
// Hosts that hand the visitor back to us mid visit: a page reached from one
// must not credit it as the source of the visit or of the purchase. This is
// GA4's "unwanted referrals" list, kept here because the Admin API cannot set it.
const UNWANTED_REFERRERS = ['checkout.stripe.com'];

let loaded = false;
let lastLocation = '';
let watching = false;
const startedForms = new Set(); // lead forms touched on this page

function gtag() {
  // Consent commands only. Tag Manager reads the arguments object itself, so
  // this stays a plain function.
  window.dataLayer.push(arguments);
}

export function isAnalyticsLoaded() {
  return loaded;
}

// The page address Google is allowed to see: origin, path and campaign tags.
export function sanitisedLocation(loc) {
  const params = new URLSearchParams(loc.search);
  const kept = new URLSearchParams();
  KEPT_PARAMS.forEach((key) => {
    if (params.has(key)) kept.set(key, params.get(key));
  });
  const query = kept.toString();
  return loc.origin + loc.pathname + (query ? '?' + query : '');
}

// Another site's address may carry its own personal data in the query.
export function sanitisedReferrer(referrer) {
  if (!referrer) return '';
  try {
    const url = new URL(referrer);
    return url.origin + url.pathname;
  } catch (e) {
    return '';
  }
}

function isUnwantedReferrer(referrer) {
  try {
    return UNWANTED_REFERRERS.indexOf(new URL(referrer).hostname) !== -1;
  } catch (e) {
    return false;
  }
}

// The kind of page a path is, 'other' when no rule matches.
export function contentGroup(path) {
  const p = String(path || '').replace(/(.)\/+$/, '$1');
  const rule = CONTENT_GROUPS.find(([prefix]) =>
    prefix.endsWith('/') ? p.startsWith(prefix) : p === prefix || p.startsWith(prefix + '/'));
  return rule ? rule[1] : 'other';
}

// The conversion a link leads to (CTA_RULES), '' for any other link.
export function ctaFor(href) {
  let url;
  try {
    url = new URL(String(href || ''), 'https://www.improve.online');
  } catch (e) {
    return '';
  }
  const rule = CTA_RULES.find(([test]) => test(url));
  return rule ? rule[1] : '';
}

// Where a link sits: a data-cta-location on it or around it, else the
// header, the footer or the page itself.
export function ctaLocation(el) {
  const marked = el.closest('[data-cta-location]');
  if (marked) return marked.getAttribute('data-cta-location');
  if (el.closest('.page-header, .v-navigation-drawer')) return 'header';
  if (el.closest('footer')) return 'footer';
  return 'page';
}

// Clicks on conversion links and the first touch on each lead form, for the
// whole site from one place: a new button is measured with no code of its
// own, and a form needs only data-analytics-form="<name>".
export function watchInteractions() {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  document.addEventListener('click', (e) => {
    const link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    const cta = link ? ctaFor(link.getAttribute('href')) : '';
    if (cta) track('cta_click', { cta, cta_location: ctaLocation(link) });
  }, true);
  document.addEventListener('focusin', (e) => {
    const form = e.target && e.target.closest ? e.target.closest('[data-analytics-form]') : null;
    if (!form || !hasAnalytics()) return;
    const name = form.getAttribute('data-analytics-form');
    if (startedForms.has(name)) return;
    startedForms.add(name);
    track('form_start', { form: name });
  }, true);
}

// The fields a Vuetify form found invalid, by their name attribute or else
// their label (never their values), comma separated; '' when there are none.
export function invalidFields(vform) {
  const inputs = (vform && vform.inputs) || [];
  const names = inputs
    .filter((i) => i.hasError || i.valid === false)
    .map((i) => String((i.$attrs && i.$attrs.name) || i.label || 'unnamed')
      .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
  return names.filter((n, idx) => n && names.indexOf(n) === idx).slice(0, 5).join(',');
}

// A lead form submit that did not go through: the fields that stopped it
// (invalidFields), or why the API refused it (serverError).
export function trackFormError(form, fields) {
  track('form_error', { form, field: fields || 'unknown' });
}

// An API refusal as form_error's field: server_422, server_429, server_500,
// or server_network when no answer came.
export function serverError(err) {
  const status = err && err.response ? err.response.status : 0;
  return 'server_' + (status || 'network');
}

// What someone looked for in the header search and how it ended (selected,
// no_results, abandoned). A term that looks like an email address or a phone
// number is never sent.
export function trackSearch(term, outcome) {
  const t = String(term || '').toLowerCase().trim().replace(/\s+/g, ' ').slice(0, 50);
  if (t.length < 2) return;
  const personal = t.indexOf('@') !== -1 || /\d{6,}/.test(t.replace(/[\s()+-]/g, ''));
  track('search', { search_term: personal ? '(not sent)' : t, search_outcome: outcome });
}

// /?improve_staff=on marks this browser as staff, =off unmarks it. Kept on
// the device only because a member of staff asked for it; it holds no data.
export function applyStaffMode(search) {
  let value = null;
  try {
    value = new URLSearchParams(search).get(STAFF_KEY);
    if (value === 'on') window.localStorage.setItem(STAFF_KEY, '1');
    if (value === 'off') window.localStorage.removeItem(STAFF_KEY);
  } catch (e) {
    /* storage blocked: nothing to mark */
  }
  return value;
}

function isStaff() {
  try {
    return window.localStorage.getItem(STAFF_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function loadAnalytics() {
  if (loaded || !GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  // Consent Mode v2, before the container: everything denied by default, then
  // only analytics granted. The ads signals stay denied: we run no advertising.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });
  gtag('consent', 'update', { analytics_storage: 'granted' });
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const env = GTM_ENV_AUTH
    ? '&gtm_auth=' + encodeURIComponent(GTM_ENV_AUTH) +
      '&gtm_preview=' + encodeURIComponent(GTM_ENV_PREVIEW) + '&gtm_cookies_win=x'
    : '';
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(GTM_ID) + env;
  document.head.appendChild(script);
}

// Sends one GA4 event when the visitor allowed analytics. `ecommerce` holds
// value, currency, items and transaction_id, for begin_checkout and purchase.
// Never put a name, an email, a company or an id that unlocks anything in
// either.
export function track(name, params = {}, ecommerce = null) {
  if (!GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  loadAnalytics();
  const event = { event: name, site: SITE, ecommerce: null, traffic_type: isStaff() ? 'internal' : undefined };
  EVENT_KEYS.forEach((key) => {
    event[key] = undefined;
  });
  Object.assign(event, params);
  if (ecommerce) {
    // Google's pattern: clear the previous basket first, or the new one is
    // merged into it.
    window.dataLayer.push({ ecommerce: null });
    event.ecommerce = ecommerce;
  }
  window.dataLayer.push(event);
}

// After each route change (with the route, so the "page not found" page is
// grouped as such), and once when consent is first given.
export function trackPageView(route) {
  if (!GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  const location = sanitisedLocation(window.location);
  const referrer = lastLocation || sanitisedReferrer(document.referrer);
  lastLocation = location;
  startedForms.clear();
  // ignore_referrer is set or cleared on every page view, so it never
  // outlives the page that came from payment.
  track('page_view', {
    page_location: location,
    page_referrer: referrer,
    page_title: document.title,
    ignore_referrer: isUnwantedReferrer(referrer) || undefined,
    content_group: route && route.name === 'NotFound' ? 'not_found' : contentGroup(window.location.pathname),
  });
}

// Refusal or withdrawal: tell the Google tag, then delete Google's cookies on
// the shared domain and on this host.
export function clearAnalytics() {
  if (typeof document === 'undefined') return;
  if (loaded) gtag('consent', 'update', { analytics_storage: 'denied' });
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => GA_COOKIE.test(name))
    .forEach((name) => {
      document.cookie = name + '=; max-age=0; path=/';
      document.cookie = name + '=; max-age=0; path=/; domain=' + SHARED_DOMAIN;
    });
}

// Google's anonymous id for this browser: the two numbers at the end of the
// _ga cookie (staging's is stg_ga, VUE_APP_GA_COOKIE). The API keeps it with a
// purchase so it can report a bank transfer to Google when it is paid, days
// after the buyer left (analytics plan Phase 3). '' without analytics consent,
// or before Google has set the cookie.
export function gaClientId() {
  if (typeof document === 'undefined' || !hasAnalytics()) return '';
  const name = process.env.VUE_APP_GA_COOKIE || '_ga';
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  const parts = match ? decodeURIComponent(match[1]).split('.') : [];
  return parts.length >= 4 ? parts.slice(-2).join('.') : '';
}

// A one way hash of the purchase id: the raw uuid unlocks the contract
// download, so it must never reach Google. Resolves '' without SubtleCrypto.
export function transactionId(uuid) {
  try {
    const bytes = new TextEncoder().encode(String(uuid));
    return window.crypto.subtle.digest('SHA-256', bytes).then((buf) =>
      Array.from(new Uint8Array(buf))
        .slice(0, 8)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
    );
  } catch (e) {
    return Promise.resolve('');
  }
}
